import type { Directive, DirectiveBinding, Ref } from 'vue'

export interface SwipeOptions {
    onSwipeRight?: () => void
    onSwipeLeft?: () => void
    threshold?: number
    maxDistance?: number
    disabled?: boolean
    swipeX?: Ref<number>
}

interface SwipeState {
    options: SwipeOptions
    startX: number
    startY: number
    currentX: number
    currentY: number
    dragging: boolean
    direction: 'horizontal' | 'vertical' | null
    suppressNextClick: boolean
    pointerDownHandler: (event: PointerEvent) => void
    pointerMoveHandler: (event: PointerEvent) => void
    endSwipeHandler: (event: PointerEvent) => void
    clickCaptureHandler: (event: MouseEvent) => void
}

// Distance (px) a horizontal drag must cross before we treat the gesture as a
// swipe and suppress the tap/click that would otherwise fire on release.
const CLICK_SUPPRESS_DISTANCE = 8

// Elements that must never initiate a swipe: form controls, links, and any
// element explicitly opted out via `data-no-swipe` (icon buttons, drag handle).
const NO_SWIPE_SELECTOR = 'input, textarea, select, a, [draggable="true"], [data-no-swipe]'

const ANGLE_THRESHOLD = 30 // degrees from vertical = horizontal swipe
const THRESHOLD_RADIANS = (ANGLE_THRESHOLD * Math.PI) / 180

const stateMap = new WeakMap<HTMLElement, SwipeState>()

function getOpts(state: SwipeState): SwipeOptions {
    return state.options
}

function createHandlers(el: HTMLElement, state: SwipeState) {
    function onPointerDown(event: PointerEvent): void {
        const opts = getOpts(state)
        if (opts.disabled) return

        const target = event.target as HTMLElement
        if (target?.closest?.(NO_SWIPE_SELECTOR)) {
            return
        }

        state.startX = event.clientX
        state.startY = event.clientY
        state.currentX = 0
        state.currentY = 0
        state.dragging = true
        state.direction = null
        state.suppressNextClick = false
        ;(event.currentTarget as HTMLElement)?.setPointerCapture?.(event.pointerId)
        el.style.transition = 'none'
    }

    function onPointerMove(event: PointerEvent): void {
        if (!state.dragging) return

        const dx = event.clientX - state.startX
        const dy = event.clientY - state.startY

        if (state.direction === null && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) {
            const angle = Math.abs(Math.atan2(Math.abs(dy), Math.abs(dx)))
            state.direction = angle > THRESHOLD_RADIANS ? 'vertical' : 'horizontal'
        }

        if (state.direction === 'vertical') {
            state.dragging = false
            state.currentX = 0
            el.style.transform = ''
            el.style.transition = ''
            return
        }

        const opts = getOpts(state)
        const maxDistance = opts.maxDistance ?? 112
        state.currentX = Math.max(-maxDistance, Math.min(maxDistance, dx))
        if (opts.swipeX) opts.swipeX.value = state.currentX
        el.style.transform = `translateX(${state.currentX}px)`
    }

    function endSwipe(_event: PointerEvent): void {
        if (!state.dragging) {
            state.currentX = 0
            state.direction = null
            return
        }
        state.dragging = false

        el.style.transition = 'transform var(--ease, cubic-bezier(0.18, 0.9, 0.2, 1)) 280ms'

        const opts = getOpts(state)
        const threshold = opts.threshold ?? 96

        if (state.currentX > threshold) {
            opts.onSwipeRight?.()
        } else if (state.currentX < -threshold) {
            opts.onSwipeLeft?.()
        }

        if (Math.abs(state.currentX) >= CLICK_SUPPRESS_DISTANCE) {
            state.suppressNextClick = true
        }

        if (opts.swipeX) opts.swipeX.value = 0
        state.currentX = 0
        state.direction = null
        el.style.transform = ''
    }

    return { onPointerDown, onPointerMove, endSwipe }
}

export const vSwipe: Directive<HTMLElement, SwipeOptions> = {
    mounted(el: HTMLElement, binding: DirectiveBinding<SwipeOptions>) {
        const options = binding.value ?? {}

        const handlers = createHandlers(el, {} as SwipeState) // placeholder, replaced below

        function onClickCapture(event: MouseEvent): void {
            if (state.suppressNextClick) {
                state.suppressNextClick = false
                event.preventDefault()
                event.stopPropagation()
            }
        }

        const state: SwipeState = {
            options,
            startX: 0,
            startY: 0,
            currentX: 0,
            currentY: 0,
            dragging: false,
            direction: null,
            suppressNextClick: false,
            pointerDownHandler: handlers.onPointerDown,
            pointerMoveHandler: handlers.onPointerMove,
            endSwipeHandler: handlers.endSwipe,
            clickCaptureHandler: onClickCapture,
        }

        // Now recreate handlers with the real state reference
        const realHandlers = createHandlers(el, state)
        state.pointerDownHandler = realHandlers.onPointerDown
        state.pointerMoveHandler = realHandlers.onPointerMove
        state.endSwipeHandler = realHandlers.endSwipe

        stateMap.set(el, state)

        el.style.touchAction = 'pan-y'
        el.style.userSelect = 'none'

        el.addEventListener('pointerdown', state.pointerDownHandler)
        el.addEventListener('pointermove', state.pointerMoveHandler)
        el.addEventListener('pointerup', state.endSwipeHandler)
        el.addEventListener('pointercancel', state.endSwipeHandler)
        el.addEventListener('lostpointercapture', state.endSwipeHandler)
        el.addEventListener('click', state.clickCaptureHandler, true)
    },

    updated(el: HTMLElement, binding: DirectiveBinding<SwipeOptions>) {
        const state = stateMap.get(el)
        if (state) {
            state.options = binding.value ?? {}
        }
    },

    unmounted(el: HTMLElement) {
        const state = stateMap.get(el)
        if (state) {
            el.removeEventListener('click', state.clickCaptureHandler, true)
        }
        stateMap.delete(el)
    },
}
